import { useEffect, useState } from "react";
import { toast } from "sonner";

import { adminEndpoints, extractErrorAr } from "@/lib/adminApi";
import Icon from "@/components/Icon";

const STATUSES = [
  { key: "", label: "الكل" },
  { key: "new", label: "جديد" },
  { key: "contacted", label: "تم التواصل" },
  { key: "completed", label: "مكتمل" },
  { key: "cancelled", label: "ملغى" },
];

const STATUS_LABEL = STATUSES.reduce((a, s) => (s.key ? { ...a, [s.key]: s.label } : a), {});
const STATUS_COLOR = {
  new: "bg-cyan-100 text-cyan-800",
  contacted: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-200 text-slate-700",
};

export default function AdminQuoteRequests() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const data = await adminEndpoints.listQuotes(params);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(extractErrorAr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filter]);

  const changeStatus = async (id, status) => {
    try {
      await adminEndpoints.updateQuoteStatus(id, status);
      toast.success("تم تحديث الحالة.");
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) {
      toast.error(extractErrorAr(err));
    }
  };

  return (
    <div data-testid="admin-quote-requests">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#0A1628]">طلبات العروض</h1>
          <p className="text-slate-500 mt-1">إجمالي: {total}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="quote-status-filter">
          {STATUSES.map((s) => (
            <button
              key={s.key || "all"}
              onClick={() => setFilter(s.key)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                filter === s.key
                  ? "bg-[#0A1628] text-white border-[#0A1628]"
                  : "bg-white text-[#0A1628] border-slate-200 hover:border-[#00E5FF]"
              }`}
              data-testid={`quote-filter-${s.key || "all"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 bg-white border border-slate-100 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-start text-sm" data-testid="quotes-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-start font-semibold">المرجع</th>
                <th className="px-4 py-3 text-start font-semibold">الاسم</th>
                <th className="px-4 py-3 text-start font-semibold">الهاتف</th>
                <th className="px-4 py-3 text-start font-semibold">المنتج / الخدمة</th>
                <th className="px-4 py-3 text-start font-semibold">الحالة</th>
                <th className="px-4 py-3 text-start font-semibold">التاريخ</th>
                <th className="px-4 py-3 text-start font-semibold">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">جاري التحميل...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">لا توجد طلبات لعرضها.</td></tr>
              )}
              {items.map((q) => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500" dir="ltr">{q.ref}</td>
                  <td className="px-4 py-3 font-semibold text-[#0A1628]">{q.name}</td>
                  <td className="px-4 py-3 text-slate-700" dir="ltr">{q.phone}</td>
                  <td className="px-4 py-3 text-slate-700">{q.product_name || q.service_slug || "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      value={q.status}
                      onChange={(e) => changeStatus(q.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-3 py-1 border-0 focus:ring-2 focus:ring-[#00E5FF] ${STATUS_COLOR[q.status] || ""}`}
                      data-testid={`quote-status-${q.id}`}
                    >
                      {Object.entries(STATUS_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs tabular-nums" dir="ltr">
                    {new Date(q.created_at).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelected(q)}
                        className="text-[#0A1628] hover:text-[#00b8cc] text-xs font-semibold"
                        data-testid={`quote-details-${q.id}`}
                      >
                        تفاصيل
                      </button>
                      <a
                        href={`https://wa.me/${String(q.phone).replace(/[^\d]/g, "")}?text=${encodeURIComponent(`مرحباً ${q.name}، أشكرك على تواصلك مع شركة أمان`)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 text-xs font-semibold"
                        data-testid={`quote-whatsapp-${q.id}`}
                      >
                        <Icon name="message-circle" size={14} />
                        واتساب
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quote-details-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading text-xl font-bold text-[#0A1628]">تفاصيل الطلب</h3>
                <p className="font-mono text-xs text-slate-500 mt-1" dir="ltr">{selected.ref}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700">
                <Icon name="x" size={22} />
              </button>
            </div>
            <dl className="space-y-3 text-sm">
              <div><dt className="text-slate-500">الاسم</dt><dd className="font-semibold text-[#0A1628]">{selected.name}</dd></div>
              <div><dt className="text-slate-500">الهاتف</dt><dd className="text-[#0A1628]" dir="ltr">{selected.phone}</dd></div>
              {selected.city && <div><dt className="text-slate-500">المدينة</dt><dd className="text-[#0A1628]">{selected.city}</dd></div>}
              {selected.service_slug && <div><dt className="text-slate-500">الخدمة</dt><dd className="text-[#0A1628]">{selected.service_slug}</dd></div>}
              {selected.product_name && <div><dt className="text-slate-500">المنتج</dt><dd className="text-[#0A1628]">{selected.product_name}</dd></div>}
              {selected.message && (
                <div>
                  <dt className="text-slate-500">الرسالة</dt>
                  <dd className="text-[#0A1628] whitespace-pre-wrap">{selected.message}</dd>
                </div>
              )}
              <div><dt className="text-slate-500">التاريخ</dt><dd className="text-[#0A1628] tabular-nums" dir="ltr">{new Date(selected.created_at).toLocaleString("en-GB")}</dd></div>
              <div>
                <dt className="text-slate-500">إشعار واتساب</dt>
                <dd data-testid="quote-notification-status">
                  {!selected.notification && <span className="text-slate-500">لا يوجد سجل إشعار.</span>}
                  {selected.notification?.success && (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                      <Icon name="check-circle" size={16} /> تم الإرسال ✓
                      {selected.notification.wamid && <span className="text-slate-500 text-xs" dir="ltr">({selected.notification.wamid.slice(0, 20)}…)</span>}
                    </span>
                  )}
                  {selected.notification && !selected.notification.success && selected.notification.error_code === "disabled" && (
                    <span className="text-slate-600">غير مفعّل — {selected.notification.error_message}</span>
                  )}
                  {selected.notification && !selected.notification.success && selected.notification.error_code !== "disabled" && (
                    <span className="text-red-700">
                      فشل ✗ {selected.notification.error_message}
                      {selected.notification.status_code && <span className="text-xs text-slate-500" dir="ltr"> (HTTP {selected.notification.status_code})</span>}
                    </span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
